import React, { ReactNode } from 'react';

import { TFunction } from 'i18next';

export const SortDirection = {
  ASC: 'asc',
  DESC: 'desc',
  NONE: 'none',
} as const;
export type SortDirection = (typeof SortDirection)[keyof typeof SortDirection];

export interface FlyTableColumn<
  T extends Record<string, any>,
  K extends keyof T = keyof T,
> {
  field?: K;
  header: ReactNode;
  render?: (value: any, row: T, index: number) => React.ReactNode;
  className?: string;
  maxWidth?: number;
  sort?: (a: T, b: T, direction: SortDirection) => number;
  sortable?: boolean;
}

export interface FlyTableProps<T extends Record<string, any>> {
  data: T[];
  columns: FlyTableColumn<T>[];
  variant?: 'surface' | 'ghost';
  emptyMessage?: string;
}

export type PaginationCursorRespone = {
  next_cursor: string;
  prev_cursor: string;
};

export type PaginationOffsetResponse = {
  total: number;
  offset: number;
  limit: number;
};

export type TableFilter = {
  name?: Parameters<TFunction>;
  key: string;
  value?: string;
};

export type TableFilterOption<T> = {
  name: Parameters<TFunction>;
  getSelectedName: (value: string | undefined) => Parameters<TFunction>;
  possibleValues: {
    name: Parameters<TFunction>;
    value: string;
    filter: (value: T) => boolean;
  }[];
  key: string;
  /** When true, this filter is not shown in the "Add filter" dropdown (e.g. custom/URL-only filters like prompt_query). */
  hideFromAddDropdown?: boolean;
  /** When true, this filter is applied client-side even when dataProcessing='server' (requires clientFilterFn on descriptor). */
  applyClientSide?: boolean;
  FilterView: React.FC<{
    filter: TableFilter;
    filterOption: TableFilterOption<T>;
    onChange: (value: string) => void;
    onRemove: () => void;
  }>;
};

/** Filter kind for declarative filter descriptors. */
export const FilterKind = {
  SEARCH: 'search',
  RADIO: 'radio',
  CHECKBOX: 'checkbox',
  CUSTOM: 'custom',
} as const;
export type FilterKind = (typeof FilterKind)[keyof typeof FilterKind];

/** Single option for radio/checkbox filters (value + i18n label). */
export type FilterOptionLabel = Parameters<TFunction>;
export type TableFilterDescriptorOption = {
  value: string;
  label: FilterOptionLabel;
  /** Sprite icon name rendered before the label (checkbox filters only). */
  icon?: string;
};

/** Static options or async getter for dynamic options (e.g. from server). */
export type TableFilterOptionsSource<T = unknown> =
  | {
      type: 'static';
      options: TableFilterDescriptorOption[];
      /** Optional client-side filter when dataProcessing='client'. */
      clientFilterFn?: (row: T, value: string) => boolean;
    }
  | {
      type: 'dynamic';
      getOptions: () =>
        | TableFilterDescriptorOption[]
        | Promise<TableFilterDescriptorOption[]>;
      /** Optional client-side filter when dataProcessing='client'. */
      filterFn?: (row: T, value: string) => boolean;
    };

/** Base shape shared by all filter descriptors. */
export type TableFilterDescriptorBase = {
  key: string;
  /** i18n: [labelKey, { defaultValue }]. */
  label: FilterOptionLabel;
  /** When true, not shown in "Add filter" dropdown. */
  hideFromAddDropdown?: boolean;
};

/** Single-value radio filter. */
export type RadioFilterDescriptor<T = unknown> = TableFilterDescriptorBase & {
  kind: typeof FilterKind.RADIO;
  optionsSource: TableFilterOptionsSource<T>;
};

/** Multi-value checkbox filter; URL stores comma-separated values. */
export type CheckboxFilterDescriptor<T = unknown> =
  TableFilterDescriptorBase & {
    kind: typeof FilterKind.CHECKBOX;
    optionsSource: TableFilterOptionsSource<T>;
  };

/** Free-text search filter (one per table); column id for client-side match is on table config. */
export type SearchFilterDescriptor = TableFilterDescriptorBase & {
  kind: typeof FilterKind.SEARCH;
};

/** Custom filter with its own FilterView; part of same state/URL. */
export type CustomFilterDescriptor<T = unknown> = TableFilterDescriptorBase & {
  kind: typeof FilterKind.CUSTOM;
  FilterView: React.FC<{
    filter: TableFilter;
    value: string | undefined;
    onChange: (value: string) => void;
    onRemove: () => void;
  }>;
  /** Optional client-side filter when dataProcessing='client'. */
  filterFn?: (row: T, value: string) => boolean;
};

/** Declarative filter descriptor; use with TanstackTable filter system. */
export type TableFilterDescriptor<T = unknown> =
  | RadioFilterDescriptor<T>
  | CheckboxFilterDescriptor<T>
  | SearchFilterDescriptor
  | CustomFilterDescriptor<T>;
